import { FC, useCallback, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { Box, Button, Center, CustomSpinner, Row, ScrollArea, Text } from '~/commonComponents';
import { Colors } from '~/themes/colors';
import { captureException } from '~/services/datadog';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

interface IPDFPreviewConsentProps {
  pdfUrl?: string;
  isLoading: boolean;
  isAccepting: boolean;
  onConsent: () => void;
  bannerText?: string;
  emptyText?: string;
  minHeight?: string | number;
  minHeightContent?: string | number;
}

const DEFAULT_BANNER_TEXT = 'Before you continue, please read and accept our Terms and Conditions.';
const DEFAULT_EMPTY_TEXT = 'No Terms And Condition required this time';

export const PDFPreviewConsent: FC<IPDFPreviewConsentProps> = ({
  pdfUrl,
  isLoading,
  isAccepting,
  onConsent,
  bannerText = DEFAULT_BANNER_TEXT,
  emptyText = DEFAULT_EMPTY_TEXT,
  minHeight,
}) => {
  const [isScrollBottom, setScrollBottom] = useState(false);
  const [numPages, setNumPages] = useState<number>(0);
  const [boxHeight, setBoxHeight] = useState<number>(0);

  const boxRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return;
      if (boxHeight) return;

      const observer = new ResizeObserver(([entry]) => {
        setBoxHeight((prev) => {
          if (entry.contentRect.height === 0) {
            return prev;
          }
          if (prev) {
            observer.disconnect();
            return prev;
          }
          return entry.contentRect.height - 100;
        });
      });
      observer.observe(node);
    },
    [boxHeight],
  );


  if (isLoading) {
    return (
      <div className="component_container" style={{ minHeight: minHeight }}>
        <div
          className="floating_container"
          style={{
            flexDirection: 'column',
            justifyContent: 'flex-start',
            marginInline: '20px',
          }}
        >
          <Center marginTop={40}>
            <CustomSpinner isLoading />
          </Center>
        </div>
      </div>
    );
  }

  if (!pdfUrl) {
    return (
      <div className="component_container" style={{ minHeight: minHeight }}>
        <div
          className="floating_container"
          style={{
            flexDirection: 'column',
            justifyContent: 'flex-start',
            marginInline: '20px',
          }}
        >
          <Center marginTop={40}>{emptyText}</Center>
        </div>
      </div>
    );
  }

  return (
    <div className="component_container" style={{ minHeight: minHeight }} data-cy="tnc-page">
      <div
        className="floating_container"
        style={{
          flexDirection: 'column',
          justifyContent: 'flex-start',
          marginInline: '20px',
        }}
      >
        <Row
          backgroundColor={Colors.primary_50}
          paddingHorizontal={14}
          paddingVertical={10}
          border={`1px solid ${Colors.primary_500}`}
          marginTop={-10}
          marginLeft={-10}
          marginRight={-10}
        >
          <Text fontSize={16} color="primary_500">
            {bannerText}
          </Text>
        </Row>
        <Box ref={boxRef} paddingBottom={10} paddingTop={10} flex={1} position="relative">
          <ScrollArea
            data-cy="tnc-scroll-area"
            style={{ flex: 1, marginTop: 16, marginBottom: 16, maxHeight: boxHeight, zIndex: 1 }}
            onEndReached={() => {
              if (numPages > 0 && boxHeight) {
                setScrollBottom(true);
              }
            }}
            threshold={60}
          >
            <Center flex={1}>
              <Document
                loading={<CustomSpinner isLoading />}
                file={pdfUrl}
                onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                onLoadError={(error) => {
                  console.error('PDF load error:', error);
                  captureException(error, { component: 'PDFPreviewConsent', pdfUrl });
                }}
              >
                {Array.from({ length: numPages }, (_, i) => (
                  <div
                    key={i + 1}
                    style={{
                      overflow: 'hidden',
                      borderBottom: `10px solid ${Colors.neutral_150}`,
                    }}
                  >
                    <Page pageNumber={i + 1} width={700} />
                  </div>
                ))}
                <Box height={20} />
              </Document>
            </Center>
          </ScrollArea>
          <Center
            paddingBottom={40}
            paddingTop={30}
            position="absolute"
            bottom={0}
            width="100%"
            zIndex={2}
            backgroundColor={Colors.white_alpha_85}
          >
            <Button
              dataCy="tnc-agree-button"
              text={isScrollBottom ? 'I Agree' : 'Scroll to Agree'}
              disabled={!isScrollBottom || isAccepting}
              isLoading={isAccepting}
              onClick={onConsent}
            />
          </Center>
        </Box>
      </div>
    </div>
  );
};
